import React from 'react';

import { addSeconds } from 'date-fns';
import { defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import { IHistoricalOrder } from '@rbi-ctg/menu';
import ActionButton from 'components/action-button';
import { ServiceMode } from 'state/order';
import { DeliveryStatus, OrderStatus } from 'state/order/types';

interface IOrderActionProps {
  order: IHistoricalOrder;
  loading: boolean;
  disabled: boolean;
  onTrack: () => void;
  onReorder: () => void;
  onFinish: () => void;
}

const Btn = styled(ActionButton)`
  width: 49%;
  padding-right: 0;
  padding-left: 0;

  :nth-of-type(n + 2) {
    margin-left: 2%;
  }
`;

const OrderAction = ({
  order,
  loading,
  disabled,
  onTrack,
  onReorder,
  onFinish
}: IOrderActionProps) => {
  const { formatMessage } = useIntl();

  const isEnRoute = () => {
    const { delivery, cart } = order;
    if (!delivery) {
      return false;
    }
    const isDelivery = cart.serviceMode === ServiceMode.DELIVERY;
    const notEnRouteStatus = new Set([
      DeliveryStatus.ORDER_CANCELLED,
      DeliveryStatus.ORDER_ABANDONED,
      DeliveryStatus.ORDER_DROPPED_OFF
    ]);
    return isDelivery && !notEnRouteStatus.has(delivery.status);
  };

  const isFinish = () => {
    const { status, cart, createdAt, fireOrderIn } = order;
    const inFinishStatus = new Set([ServiceMode.TAKEOUT, ServiceMode.CURBSIDE]);
    const isActive = !!fireOrderIn && addSeconds(new Date(createdAt), fireOrderIn) > new Date();
    return (
      inFinishStatus.has(cart.serviceMode) && isActive && status === OrderStatus.INSERT_SUCCESSFUL
    );
  };

  if (isEnRoute()) {
    return (
      <Btn data-testid="track-order-button" onClick={onTrack} disabled={disabled}>
        {formatMessage(messages.trackOrder)}
      </Btn>
    );
  }

  if (isFinish()) {
    return (
      <Btn data-testid="finish-order-button" onClick={onFinish} disabled={disabled}>
        {formatMessage(messages.finishOrder)}
      </Btn>
    );
  }

  return (
    <Btn data-testid="reorder-button" onClick={onReorder} isLoading={loading} disabled={disabled}>
      {formatMessage(messages.reorder)}
    </Btn>
  );
};

export default OrderAction;
const messages = defineMessages({
  reorder: {
    id: 'reorder',
    defaultMessage: 'Reorder',
    description: 'Button to reorder food'
  },

  finishOrder: {
    id: 'finishOrder',
    defaultMessage: 'Finish order',
    description: 'Button to finish current order'
  },

  trackOrder: {
    id: 'trackOrder',
    defaultMessage: 'Track order',
    description: 'Button to track their current order'
  }
});
